/**
 * Dynamic-track position store — a Zustand buffer of recent per-sensor
 * positions, the source the :class:`TrackTailLayer` renders trimmed to
 * the configured km (issue #278 follow-up, PR1).
 *
 * # Why a store
 *
 * Mirrors :file:`map/track-store`'s sibling stores (events-store,
 * selection-store): a module-level Zustand store fed by the live SSE
 * status bridge (:func:`map/use-track-sse-bridge`). Per-producer
 * position lists accumulate as status updates arrive; the layer trims
 * each to the configured km **at render** (so changing the length in the
 * settings modal re-trims instantly without re-accumulating).
 *
 * PR1 feeds the store from live SSE only (tails build up over the next
 * few updates). PR2 adds a one-time server backfill so tails appear
 * fully-formed on load.
 *
 * # Bounding
 *
 * Each producer's list is a ring capped at
 * :data:`TRACK_MAX_POINTS_PER_PRODUCER` (newest kept). The visible tail
 * is bounded much tighter by the km-trim at render; the cap only guards
 * memory under a long-lived session. Appends are monotonic by ``time``
 * (a duplicate / out-of-order status is ignored), so JetStream
 * at-least-once redelivery doesn't double-plot a point.
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state).
 *   - map/use-track-sse-bridge.ts (the live feeder).
 *   - map/track-tail-layer.tsx (the consumer + km-trim).
 */
import { useMemo } from "react";
import { create } from "zustand";

/**
 * Safety cap on per-producer points. The km-trim at render keeps the
 * visible tail short; this only bounds memory for a long session. At the
 * default 10 km / typical sensor cadence this is far more than needed.
 */
export const TRACK_MAX_POINTS_PER_PRODUCER = 1000;

/** One recent position in a sensor's track. */
export interface TrackPoint {
  readonly latitude_deg: number;
  readonly longitude_deg: number;
  /** Producer-side detection timestamp, ISO-8601 UTC. Monotonic per producer. */
  readonly time: string;
  /** SAPIENT health_state at this point; the tail layer colours by the
   *  latest point's value, matching the marker + selected-trail. */
  readonly health_state: number;
}

/** The track store shape. */
export interface TrackState {
  /** Recent positions per producer id, oldest-first. */
  readonly byProducer: Record<string, readonly TrackPoint[]>;
  /**
   * Append one position to a producer's track. No-op when ``point.time``
   * is not strictly newer than the producer's last point (dedup /
   * out-of-order guard). Rings to {@link TRACK_MAX_POINTS_PER_PRODUCER}.
   */
  readonly append: (producerId: string, point: TrackPoint) => void;
  /**
   * Seed a producer's track with a batch of backfilled points (PR2
   * viewport backfill — {@link useTracksFeed}). Unlike {@link append}'s
   * strictly-newer monotonic guard, ``seed`` **merges** the batch with any
   * already-accumulated points: union, dedupe by ``time`` (one plot per
   * timestamp), sort ascending, then ring to
   * {@link TRACK_MAX_POINTS_PER_PRODUCER} (newest kept). This is what lets
   * the server backfill (older history) slot in *front* of live-SSE points
   * that may have arrived first. An empty batch is a no-op (the producer's
   * existing list keeps its reference, so an empty backfill doesn't churn).
   */
  readonly seed: (producerId: string, points: readonly TrackPoint[]) => void;
  /** Empty the store (e.g. on sign-out / network switch). */
  readonly clear: () => void;
}

/**
 * The module-level track store. Consume via {@link useAllTracks}; the SSE
 * bridge writes via ``useTrackStore.getState().append(...)``.
 */
export const useTrackStore = create<TrackState>((set) => ({
  byProducer: {},
  append: (producerId, point) =>
    set((state) => {
      const existing = state.byProducer[producerId] ?? [];
      const last = existing[existing.length - 1];
      // Monotonic guard: ignore a duplicate or out-of-order timestamp.
      if (last !== undefined && Date.parse(point.time) <= Date.parse(last.time)) {
        return state;
      }
      let next = [...existing, point];
      if (next.length > TRACK_MAX_POINTS_PER_PRODUCER) {
        next = next.slice(next.length - TRACK_MAX_POINTS_PER_PRODUCER);
      }
      return { byProducer: { ...state.byProducer, [producerId]: next } };
    }),
  seed: (producerId, points) =>
    set((state) => {
      // Empty backfill: leave the producer's list (and its reference)
      // untouched so a no-data viewport response doesn't churn the store.
      if (points.length === 0) {
        return state;
      }
      const existing = state.byProducer[producerId] ?? [];
      // Union + dedupe by ``time``. A Map keyed on the ISO timestamp keeps
      // exactly one point per instant; inserting ``existing`` first then the
      // backfill means a colliding backfill sample wins (both describe the
      // same status message, so the position is identical either way).
      const byTime = new Map<string, TrackPoint>();
      for (const p of existing) {
        byTime.set(p.time, p);
      }
      for (const p of points) {
        byTime.set(p.time, p);
      }
      let merged = [...byTime.values()].sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
      if (merged.length > TRACK_MAX_POINTS_PER_PRODUCER) {
        merged = merged.slice(merged.length - TRACK_MAX_POINTS_PER_PRODUCER);
      }
      return { byProducer: { ...state.byProducer, [producerId]: merged } };
    }),
  clear: () => {
    set({ byProducer: {} });
  },
}));

/**
 * The full per-producer track map. Subscribes to ``byProducer`` (a stable
 * reference between mutations) so the consuming layer re-renders only when
 * a track actually changes.
 */
export function useAllTracks(): Record<string, readonly TrackPoint[]> {
  const byProducer = useTrackStore((state) => state.byProducer);
  // Identity-stable between mutations; ``useMemo`` is a no-op guard that
  // keeps the return referentially equal to ``byProducer`` itself.
  return useMemo(() => byProducer, [byProducer]);
}
